import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Form, 
  Input, 
  Button, 
  Select, 
  message, 
  List, 
  Space, 
  Popconfirm, 
  Modal, 
  Tag, 
  Divider, 
  Alert,
  Badge,
  Typography,
  Row,
  Col
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  BarChartOutlined,
  CheckCircleOutlined,
  SettingOutlined
} from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/apiService';

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

const ScorecardManager = ({ projectId = null }) => {
  const [scorecards, setScorecards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingScorecard, setEditingScorecard] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const { getAuthToken } = useAuth();

  useEffect(() => {
    loadScorecards();
  }, [projectId]);

  const loadScorecards = async () => {
    try {
      setLoading(true);
      const token = getAuthToken();
      const response = await apiService.getAllScorecards(token, projectId);
      setScorecards(response.data || []);
    } catch (error) {
      console.error('Failed to load scorecards:', error);
      if (error.message !== 'Failed to get scorecards') {
        message.error('Failed to load scorecards');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEdit = (scorecard = null) => {
    setEditingScorecard(scorecard);
    if (scorecard) {
      form.setFieldsValue({
        scorecard_number: scorecard.scorecard_number,
        name: scorecard.name,
        description: scorecard.description,
        outcome_type: scorecard.outcome_type,
        instructions: scorecard.instructions
      });
    } else {
      form.resetFields();
    }
    setIsModalVisible(true);
  };

  const handleSave = async (values) => {
    try {
      setLoading(true);
      const token = getAuthToken();

      const scorecardData = {
        ...values,
        project_id: projectId
      };

      if (editingScorecard) {
        await apiService.updateScorecard(editingScorecard.id, scorecardData, token);
        message.success('Scorecard updated successfully!');
      } else {
        await apiService.createScorecard(scorecardData, token, projectId);
        message.success('Scorecard created successfully!');
      }

      setIsModalVisible(false);
      setEditingScorecard(null);
      form.resetFields();
      await loadScorecards();
    } catch (error) {
      console.error('Failed to save scorecard:', error);
      message.error(error.message || 'Failed to save scorecard');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (scorecardId) => {
    try {
      setLoading(true);
      const token = getAuthToken();
      await apiService.deleteScorecard(scorecardId, token);
      message.success('Scorecard deleted successfully!');
      await loadScorecards();
    } catch (error) {
      console.error('Failed to delete scorecard:', error);
      message.error('Failed to delete scorecard');
    } finally {
      setLoading(false);
    }
  };

  const getOutcomeTypeTag = (outcomeType) => {
    return outcomeType === 'score' ? 
      <Tag color="blue" icon={<BarChartOutlined />}>Score (1-10)</Tag> : 
      <Tag color="green" icon={<CheckCircleOutlined />}>Match/No Match</Tag>;
  };

  const getUsedScorecardNumbers = () => {
    return scorecards.map(s => s.scorecard_number);
  };

  const validateScorecardNumber = (_, value) => {
    if (!value) {
      return Promise.reject(new Error('Please enter a scorecard number'));
    }
    
    // Allow alphanumeric characters, dashes, underscores, and dots
    const validPattern = /^[a-zA-Z0-9._-]+$/;
    if (!validPattern.test(value)) {
      return Promise.reject(new Error('Scorecard number can only contain letters, numbers, dots, dashes, and underscores'));
    }
    
    // Check if number already exists (excluding current editing scorecard)
    const exists = scorecards.some(sc => 
      sc.scorecard_number === value && 
      (!editingScorecard || sc.id !== editingScorecard.id)
    );
    
    if (exists) {
      return Promise.reject(new Error('This scorecard number already exists'));
    }
    
    return Promise.resolve();
  };

  const renderScorecardCard = (scorecard) => (
    <Card
      key={scorecard.id}
      size="small"
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Tag color="#8b5cf6" style={{ marginRight: 8 }}>#{scorecard.scorecard_number}</Tag>
            <span>{scorecard.name}</span>
          </div>
          {getOutcomeTypeTag(scorecard.outcome_type)}
        </div>
      }
      extra={
        <Space>
          <Button 
            type="text" 
            size="small" 
            icon={<EditOutlined />}
            onClick={() => handleCreateEdit(scorecard)}
          />
          <Popconfirm
            title="Are you sure you want to delete this scorecard?"
            onConfirm={() => handleDelete(scorecard.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button 
              type="text" 
              size="small" 
              icon={<DeleteOutlined />}
              danger
            />
          </Popconfirm>
        </Space>
      }
      style={{ marginBottom: 16 }}
    >
      {scorecard.description && (
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          {scorecard.description}
        </Text>
      )}
      
      <div style={{ background: '#f8f9fa', padding: 12, borderRadius: 6, fontSize: '13px' }}>
        <Text strong>Instructions:</Text>
        <div style={{ marginTop: 4, color: '#666' }}>
          {scorecard.instructions.length > 200 
            ? `${scorecard.instructions.substring(0, 200)}...` 
            : scorecard.instructions}
        </div>
      </div>
    </Card>
  );

  return (
    <div className="scorecard-manager">
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
              <SettingOutlined style={{ color: '#8b5cf6' }} />
              {projectId ? 'Project Scorecards' : 'Global Scorecards'}
            </h3>
            <p style={{ margin: '4px 0 0 0', color: '#666', fontSize: '14px' }}>
              Create and manage custom evaluation criteria with specific instructions for each scorecard.
            </p>
          </div>
          
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={() => handleCreateEdit()}
            style={{ backgroundColor: '#8b5cf6', borderColor: '#8b5cf6' }}
          >
            Add Scorecard
          </Button>
        </div>

        {scorecards.length === 0 && (
          <Alert
            message="No scorecards defined"
            description="Create scorecards to define custom evaluation criteria with specific instructions. Use flexible numbering like 1, 2A, security-check, etc."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}


      </div>

      <Row gutter={[16, 16]}>
        {scorecards
          .sort((a, b) => a.scorecard_number - b.scorecard_number)
          .map(scorecard => (
            <Col span={12} key={scorecard.id}>
              {renderScorecardCard(scorecard)}
            </Col>
          ))}
      </Row>

      <Modal
        title={editingScorecard ? 'Edit Scorecard' : 'Create New Scorecard'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingScorecard(null);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
        >
          <Form.Item
            name="scorecard_number"
            label="Scorecard Number/ID"
            rules={[
              { validator: validateScorecardNumber }
            ]}
            help="Use any identifier like: 1, 2A, compliance-check, security-1.2, etc."
          >
            <Input 
              placeholder="Enter scorecard number (e.g., 1, 2A, security-check)" 
              maxLength={50}
              disabled={!!editingScorecard}
            />
          </Form.Item>

          <Form.Item
            name="name"
            label="Scorecard Name"
            rules={[{ required: true, message: 'Please enter a scorecard name' }]}
          >
            <Input placeholder="e.g., Policy Compliance, Security Assessment" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description (Optional)"
          >
            <Input placeholder="Brief description of this scorecard's purpose" />
          </Form.Item>

          <Form.Item
            name="outcome_type"
            label="Outcome Type"
            rules={[{ required: true, message: 'Please select an outcome type' }]}
          >
            <Select placeholder="Select outcome type">
              <Option value="score">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <BarChartOutlined style={{ color: '#1890ff' }} />
                  <span><strong>Score (1-10)</strong> - Numerical scoring system</span>
                </div>
              </Option>
              <Option value="match">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  <span><strong>Match/No Match</strong> - Binary evaluation</span>
                </div>
              </Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="instructions"
            label="Evaluation Instructions"
            rules={[{ required: true, message: 'Please enter evaluation instructions' }]}
          >
            <TextArea
              rows={6}
              placeholder="Provide detailed instructions for how this scorecard should be evaluated..."
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <Space style={{ float: 'right' }}>
              <Button onClick={() => {
                setIsModalVisible(false);
                setEditingScorecard(null);
                form.resetFields();
              }}>
                Cancel
              </Button>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={loading}
                style={{ backgroundColor: '#8b5cf6', borderColor: '#8b5cf6' }}
              >
                {editingScorecard ? 'Update Scorecard' : 'Create Scorecard'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ScorecardManager;